import "maplibre-gl/dist/maplibre-gl.css"
import React, { useCallback, useEffect, useRef, useState, useMemo, ReactNode } from "react"
import {
    selectActivatedFireId,
    selectControllerOverrides,
    selectIsPictureInPicture,
    useMapStylesJSON,
} from "./control/selectors"
import { setWeather } from "./control/slice"
import { get, throttle } from "nodash"
import { PartialViewState } from "./control/types"
import { Map, MapLayerMouseEvent, MapRef, ViewState } from "react-map-gl/dist/maplibre"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import {
    IconGhostId,
    maxZoom,
    minZoom,
    minZoomOnTerrain,
    maxZoomOnTerrain,
    PUBSUB_CURSOR_THROTTLE_MS,
    zIndex,
    FirePerimeterMethod,
    ToolName,
} from "@nova/constants"
import { PDFMapSnapshot } from "plugins/PDFv2/PDFMapSnapshot"
import { getSnapshotState } from "runlevel/actions"
import { MapSnapshot } from "@nova/component-map-snapshot"
import { selectActiveToolName } from "plugins/Toolbox/tool-registry"
import { selectActiveImageIds } from "./layers/selectors"
import { selectColorFinderListeningToKeyboardEvents } from "@nova/slice-color-finder"
import { MapLibreEvent, StyleSpecification } from "maplibre-gl"
import { updateMouse } from "tools/events/updateMouse"
import { selectUserID } from "user/profile/selectors"
import { useUpdateCursorRef } from "tools/events/useCursorProvider"
import {
    getViewport,
    getViewState,
    notifyGlobalMapLoaded,
    setGlobalMapRef,
    updateViewport,
    useViewport,
    viewStateEventEmitter,
    ViewStateOptions,
} from "./GlobalViewState"
import { selectSnapshotOpen } from "plugins/PDFv2/PDFSlice"
import { popCursorStack, pushCursorStack, resetCursorStack } from "./MapElements"
import { calculateTrigonmetricDistance } from "@nova/calculate-geospatial"
import {
    getGlobalTerrainData,
    setGlobalTerrainData,
    useGlobalTerrainData,
} from "./GlobalTerrainManager"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { selectUpdateMethod } from "plugins/DrawPerimeter/FirePerimeterSlice"
import { useLocation } from "react-router"
import { useRouter } from "pages"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { Rnd } from "react-rnd"
import { IconButton } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { useObserveElementRect } from "@nova/toolkit-pure-hooks"
import { useZoomToPipFlight } from "plugins/Video/shared/useZoomToPipFlight"
import { selectFollowingEntity } from "plugins/shared/playback/state/selectors"
import { setFollowingEntity } from "plugins/shared/playback/state/slice"
import { EntityFollower } from "features/map/EntityFollower"

const id = "map"

function getFlyToDuration(current: PartialViewState, next: PartialViewState) {
    const distance =
        calculateTrigonmetricDistance(
            {
                latitude: current.latitude,
                longitude: current.longitude,
            },
            {
                latitude: next.latitude,
                longitude: next.longitude,
            }
        ) / 1000
    if (distance > 100) return 0 // jump straight to the target if distance is bigger then 100km
    const zoomDelta = Math.abs(next.zoom - current.zoom)
    const distanceWithZoom = distance + zoomDelta * 10
    const minDurationMs = 1000
    const maxDurationMs = 2000

    // Normalize to [0, 1]
    const normalizedDistance = Math.min(Math.max(distanceWithZoom / 100, 0), 1)
    return minDurationMs + normalizedDistance * (maxDurationMs - minDurationMs)
}

const MINIMAP_MIN_WIDTH = 200
const MINIMAP_MIN_HEIGHT = 200

export default function MapViewStateController({ children }: { children: ReactNode }) {
    const dispatch = useAppDispatch()
    const { terrainState: isTerrainActive } = useGlobalTerrainData(["terrainState"])
    const mapStylesJSON = useMapStylesJSON()
    mapStylesJSON.terrain = getGlobalTerrainData().terrainStyle
    const controllerOverrides = useAppSelector(selectControllerOverrides)
    const colorFinderListeningToKeyboardEvents = useAppSelector(
        selectColorFinderListeningToKeyboardEvents()
    )
    const [initialViewState, setInitialViewState] = useState(getViewState())
    const selectedUploadIds = Object.keys(useAppSelector(selectActiveImageIds) || {})

    const projectId = useAppSelector(selectActivatedFireId) || ""
    const userId = useAppSelector(selectUserID)

    const isPictureInPicture = useAppSelector(selectIsPictureInPicture)
    const pdfSnapshotOpen = useAppSelector(selectSnapshotOpen())

    const activeToolName = useAppSelector(selectActiveToolName())
    const updateMethod = useAppSelector(selectUpdateMethod())

    useEffect(() => {
        if (Object.keys(controllerOverrides).length > 0) mapRef.current?.panBy([0, 0])
    }, [controllerOverrides])
    useEffect(() => {
        if (
            activeToolName &&
            (activeToolName !== ToolName.FirePerimeter ||
                updateMethod === FirePerimeterMethod.Manual) &&
            activeToolName !== ToolName.PDF &&
            activeToolName !== ToolName.Comment
        )
            pushCursorStack("crosshair")
        else resetCursorStack()
    }, [activeToolName, updateMethod])

    const isPlacingComment = activeToolName === ToolName.Comment
    const onMouseDown = useCallback(
        (e: MapLayerMouseEvent) =>
            e.originalEvent.button === 0 && !isPlacingComment && pushCursorStack("grab"),
        [isPlacingComment]
    )
    const onMouseUp = useCallback(
        (e: MapLayerMouseEvent) =>
            e.originalEvent.button === 0 && !isPlacingComment && popCursorStack(),
        [isPlacingComment]
    )

    const mapRef = useRef<MapRef | null>(null)
    useUpdateCursorRef(id)

    const followingEntity = useAppSelector(selectFollowingEntity)
    const hasCancelledFollowing = useRef(false)
    const trackViewState = useCallback(
        ({
            viewState: newViewState,
            originalEvent,
        }: {
            viewState: ViewState
            originalEvent?: unknown
        }) => {
            if (originalEvent && !(originalEvent instanceof WheelEvent)) {
                hasCancelledFollowing.current = true
                dispatch(setFollowingEntity(undefined))
            }
            updateViewport(newViewState)
        },
        [dispatch]
    )

    useEffect(() => {
        const onNewViewState = (
            viewState: PartialViewState,
            options: ViewStateOptions = {},
            originalViewState: PartialViewState
        ) => {
            if (!mapRef.current) return setInitialViewState(viewState)
            const { disableFly } = options
            const flyDuration = getFlyToDuration(originalViewState, viewState)
            const params = {
                center: [viewState.longitude, viewState.latitude] as [number, number],
                zoom: viewState.zoom,
                bearing: viewState.bearing ?? 0,
                pitch: viewState.pitch ?? 0,
            }
            if (!getGlobalTerrainData().terrainState && !disableFly && flyDuration)
                mapRef.current.flyTo({
                    ...params,
                    duration: flyDuration,
                })
            else mapRef.current.jumpTo(params)
        }
        viewStateEventEmitter.on("writeViewState", onNewViewState)
        return () => {
            viewStateEventEmitter?.off("writeViewState", onNewViewState)
        }
    }, [])

    const location = useLocation()
    const router = useRouter("/")
    const workspaceId = useAppSelector(selectActiveWorkspaceId()) || ""
    const isInProject =
        router.preview({
            path: "/workspace/:workspaceId/project/:projectId",
            params: { workspaceId, projectId },
        }) === location.pathname

    useEffect(() => {
        if (pdfSnapshotOpen) {
            setGlobalTerrainData({
                isDisabled: true,
                terrainState: false,
            })
            dispatch(setWeather(false))
        } else {
            setGlobalTerrainData({ isDisabled: false })
        }
    }, [pdfSnapshotOpen, dispatch])

    //publish current users mouse location at the defined interval
    const onMouseMove = throttle((e: MapLayerMouseEvent) => {
        if (isInProject && projectId && userId) {
            updateMouse({ mouse: { coordinates: [e.lngLat.lng, e.lngLat.lat] }, projectId, userId })
        }
    }, PUBSUB_CURSOR_THROTTLE_MS)

    //sets up event listeners for navigating the map externally
    useEffect(() => {
        //handles panning with middle mouse
        const handleMiddleMousePan = (event: MouseEvent) => {
            if (event.button !== 1) return
            pushCursorStack("all-scroll")
            const elementId = get(event, "target.nearestViewportElement.id")
            const className = get(event, "target.className")
            if (elementId !== IconGhostId && className !== "maplibregl-canvas") return
            const x = event.clientX
            const y = event.clientY
            const viewport = getViewport()
            const [lng, lat] = viewport.unproject([x, y])
            mapRef.current?.flyTo({ center: [lng, lat] })
            setTimeout(() => popCursorStack(), 300)
        }

        document.body.addEventListener("mousedown", handleMiddleMousePan)
        return () => {
            document.body.removeEventListener("mousedown", handleMiddleMousePan)
        }
    }, [])

    const reinitializeOnMapLoad = useRef(true)
    const handleMapLoad = useCallback(
        (mapEvent: MapLibreEvent<unknown>) => {
            const map = mapEvent.target
            map.resize()
            if (reinitializeOnMapLoad.current) {
                reinitializeOnMapLoad.current = false
                map.jumpTo({
                    center: [initialViewState.longitude, initialViewState.latitude],
                    zoom: initialViewState.zoom,
                    bearing: initialViewState.bearing ?? 0,
                    pitch: initialViewState.pitch ?? 0,
                })
            }
            setGlobalTerrainData({ terrainMapIsLoaded: true })
            notifyGlobalMapLoaded()
        },
        [
            initialViewState.bearing,
            initialViewState.latitude,
            initialViewState.longitude,
            initialViewState.pitch,
            initialViewState.zoom,
        ]
    )
    // Handles swapping contraints for the maplibre map controls and styles when toggling terrain state
    useEffectUnsafe(() => {
        const map = mapRef.current?.getMap()
        if (!map) return
        // Once the map has finished rendering the terrain, layers can be loaded
        map.once("styledata", () => {
            setGlobalTerrainData({ terrainMapIsLoaded: false })
        })
        // Try to load the map gracefully (no user interaction)
        // the setPitch() triggers a render cycle for maplibre, so it becomes aware of terrain elevation
        map.once("idle", () => {
            setGlobalTerrainData({ terrainMapIsLoaded: true })
            map.setZoom(map.getZoom() + 0.001)
        })
        // Fallback if the user wants to move right away
        map.once("drag", () => {
            setGlobalTerrainData({ terrainMapIsLoaded: true })
        })

        const style = mapStylesJSON as StyleSpecification
        const constrainedMinZoom = isTerrainActive ? minZoomOnTerrain : minZoom
        const constrainedMaxZoom = isTerrainActive ? maxZoomOnTerrain : maxZoom
        const currentZoom = map.getZoom()

        // Setting zoom constraints properly requires the map to not be moving
        // This logic prevents scenarios where the zoom level is constrained but terrain is inactive and vice versa
        const setConstraintOnMoveEnd = () => {
            map.off("moveend", setConstraintOnMoveEnd)
            map.setMinZoom(constrainedMinZoom)
            map.setMaxZoom(constrainedMaxZoom)
        }
        map.once("moveend", setConstraintOnMoveEnd)

        map.setStyle(style, { diff: true })

        // Forces map to calculate the new state
        // to ensure the camera registers a new state and guarantees 'moveend' fires.
        map.jumpTo({
            zoom: currentZoom <= constrainedMinZoom ? constrainedMinZoom : currentZoom,
            center: map.getCenter(),
        })
    }, [isTerrainActive])

    useEffect(() => {
        setGlobalMapRef(mapRef)
        return () => {
            setGlobalMapRef(mapRef)
            setGlobalTerrainData({ terrainState: false })
        }
    }, [])

    const MapComponent = useMemo(() => {
        return (
            <Map
                id={id}
                ref={mapRef}
                onMove={trackViewState}
                initialViewState={getViewState()}
                mapStyle={mapStylesJSON as StyleSpecification}
                maxZoom={maxZoom}
                minZoom={minZoom}
                maxPitch={60}
                {...controllerOverrides}
                dragPan={
                    (activeToolName !== ToolName.FirePerimeter ||
                        updateMethod !== FirePerimeterMethod.Manual) &&
                    (controllerOverrides.dragPan !== undefined ? controllerOverrides.dragPan : true)
                }
                attributionControl={false}
                logoPosition={"bottom-right"}
                onMouseMove={onMouseMove}
                onMouseDown={onMouseDown}
                onMouseUp={onMouseUp}
                onLoad={handleMapLoad}
                keyboard={!colorFinderListeningToKeyboardEvents && selectedUploadIds.length !== 1}
                doubleClickZoom={false}
                canvasContextAttributes={{ preserveDrawingBuffer: true }}
            >
                {children}
            </Map>
        )
    }, [
        activeToolName,
        children,
        colorFinderListeningToKeyboardEvents,
        controllerOverrides,
        handleMapLoad,
        mapStylesJSON,
        onMouseDown,
        onMouseMove,
        onMouseUp,
        selectedUploadIds.length,
        trackViewState,
        updateMethod,
    ])

    const [isMinimapOpen, setIsMinimapOpen] = useState(true)
    const [initialSize] = useState(() => ({
        width: Math.max(Math.round(window.innerWidth * 0.28), MINIMAP_MIN_WIDTH),
        height: Math.max(Math.round(window.innerWidth * 0.22), MINIMAP_MIN_HEIGHT),
    }))
    const [expandedSize, setExpandedSize] = useState(initialSize)
    const [pipPosition, setPipPosition] = useState({ x: 0, y: 0 })
    const { ref: headerRef, height: headerHeight } = useObserveElementRect()
    const pipSize = {
        width: expandedSize.width,
        height: isMinimapOpen ? expandedSize.height : headerHeight,
    }
    const { readyToZoom, zoomTo: zoomToPipFlight } = useZoomToPipFlight()
    const [shouldAutozoom, setShouldAutozoom] = useState(true)
    useEffect(() => {
        if (readyToZoom && isPictureInPicture && shouldAutozoom) {
            zoomToPipFlight()
            setShouldAutozoom(false)
        }
        if (!isPictureInPicture && !shouldAutozoom) {
            setShouldAutozoom(true)
        }
    }, [isPictureInPicture, readyToZoom, shouldAutozoom, zoomToPipFlight])
    if (isPictureInPicture)
        return (
            <div
                style={{ zIndex: zIndex.LiveVideoMap }}
                className="w-full h-full relative pointer-events-none"
                id="pip-rnd-bounds"
            >
                {followingEntity && (
                    <EntityFollower
                        id={followingEntity.id}
                        type={followingEntity.type}
                        mapRef={mapRef}
                        hasCancelledFollow={hasCancelledFollowing}
                    />
                )}
                <div
                    className="absolute bottom-4 right-4"
                    style={{
                        width: `${initialSize.width}px`,
                        height: `${initialSize.height}px`,
                    }}
                >
                    <Rnd
                        size={pipSize}
                        minWidth={MINIMAP_MIN_WIDTH}
                        minHeight={isMinimapOpen ? MINIMAP_MIN_HEIGHT : undefined}
                        position={pipPosition}
                        onResizeStop={(e, direction, ref, delta, position) => {
                            setExpandedSize({ width: ref.offsetWidth, height: ref.offsetHeight })
                            setPipPosition(position)
                        }}
                        onDragStop={(e, d) => setPipPosition({ x: d.x, y: d.y })}
                        dragHandleClassName="drag-handle"
                        enableResizing={{
                            bottom: isMinimapOpen,
                            bottomLeft: isMinimapOpen,
                            bottomRight: isMinimapOpen,
                            left: true,
                            right: true,
                            top: isMinimapOpen,
                            topLeft: isMinimapOpen,
                            topRight: isMinimapOpen,
                        }}
                        bounds="#pip-rnd-bounds"
                        className="border border-white-200 rounded-lg overflow-hidden bg-gray-800 pointer-events-auto"
                    >
                        <div className="flex flex-col h-full w-full">
                            <div
                                ref={headerRef}
                                className="px-4 py-3 text-white drag-handle flex gap-3 items-center cursor-move"
                            >
                                <div className="pb-px">Minimap</div>
                                {readyToZoom && isMinimapOpen && (
                                    <IconButton onClick={zoomToPipFlight}>
                                        <Icons.ExtraSmallIcon
                                            Icon={Icons.ZoomTo}
                                            className="text-gray-300 hover:text-white transition-colors"
                                        />
                                    </IconButton>
                                )}
                                <div className="grow" />
                                <IconButton
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={() => setIsMinimapOpen((p) => !p)}
                                >
                                    <Icons.Icon
                                        Icon={isMinimapOpen ? Icons.CollapseAll : Icons.ExpandAll}
                                        className="text-gray-300 hover:text-white transition-colors"
                                    />
                                </IconButton>
                            </div>
                            <div className="w-full h-px bg-white-200" />
                            <div className="flex-1 min-h-0 relative">{MapComponent}</div>
                        </div>
                    </Rnd>
                </div>
            </div>
        )

    return (
        <>
            <div className="w-full h-full">
                {followingEntity && (
                    <EntityFollower
                        id={followingEntity.id}
                        type={followingEntity.type}
                        mapRef={mapRef}
                        hasCancelledFollow={hasCancelledFollowing}
                    />
                )}
                {MapComponent}
                {getSnapshotState() && (
                    <MapSnapshot
                        useViewport={useViewport}
                        {...getSnapshotState()}
                        maxHeight={0.8}
                        maxWidth={0.9}
                    />
                )}
                {pdfSnapshotOpen && <PDFMapSnapshot />}
            </div>
        </>
    )
}
